import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faMapMarkerAlt,
	faPhone,
	faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import {
	faFacebookF,
	faInstagram,
	faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import emailjs from "@emailjs/browser";
import validator from "validator";
import xss from "xss";
import DOMPurify from "dompurify";

// Initialize EmailJS with public key
emailjs.init("IbAD2eHIw7cNzVX2v");

// Rate limiting implementation
const RATE_LIMIT_MINUTES = 5;
const MAX_ATTEMPTS = 3;

function Contact() {
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		enquiryType: "General Enquiry",
		message: "",
	});
	const [feedback, setFeedback] = useState("");
	const [loading, setLoading] = useState(false);
	const [attempts, setAttempts] = useState(0);
	const [lastAttemptTime, setLastAttemptTime] = useState(null);
	const [error, setError] = useState("");

	// Handle input changes for controlled fields
	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	// Input validation function
	const validateInputs = () => {
		if (!validator.isEmail(formData.email)) {
			setError("Please enter a valid email address");
			return false;
		}
		if (!validator.isMobilePhone(formData.phone)) {
			setError("Please enter a valid phone number");
			return false;
		}
		return true;
	};

	// Sanitize input data
	const sanitizeData = (data) => {
		return {
			...data,
			firstName: DOMPurify.sanitize(xss(data.firstName.trim())),
			lastName: DOMPurify.sanitize(xss(data.lastName.trim())),
			email: DOMPurify.sanitize(xss(data.email.trim())),
			phone: DOMPurify.sanitize(xss(data.phone.trim())),
			message: DOMPurify.sanitize(xss(data.message.trim())),
			enquiryType: DOMPurify.sanitize(xss(data.enquiryType)),
		};
	};

	// Check rate limiting
	const checkRateLimit = () => {
		const now = Date.now();
		if (lastAttemptTime && attempts >= MAX_ATTEMPTS) {
			const timeDiff = now - lastAttemptTime;
			const minutesPassed = timeDiff / (1000 * 60);

			if (minutesPassed < RATE_LIMIT_MINUTES) {
				setError(
					`Too many attempts. Please try again in ${Math.ceil(RATE_LIMIT_MINUTES - minutesPassed)} minutes`,
				);
				return false;
			}
			// Reset attempts after time limit
			setAttempts(0);
		}
		return true;
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		// Check rate limiting
		if (!checkRateLimit()) {
			return;
		}

		// Validate inputs
		if (!validateInputs()) {
			return;
		}

		setLoading(true);

		try {
			// Sanitize form data
			const sanitizedData = sanitizeData(formData);

			const templateParams = {
				from_name: `${sanitizedData.firstName} ${sanitizedData.lastName}`,
				from_email: sanitizedData.email,
				phone: sanitizedData.phone,
				enquiry_type: sanitizedData.enquiryType,
				message: sanitizedData.message,
			};

			await emailjs.send("service_46j5sqe", "template_1r26ouo", templateParams);

			setFeedback("Message sent successfully!");
			// Reset form fields
			setFormData({
				firstName: "",
				lastName: "",
				email: "",
				phone: "",
				enquiryType: "General Enquiry",
				message: "",
			});
		} catch (error) {
			console.error("Error sending email:", error);
			setFeedback("Error sending message. Please try again.");
			// Update rate limiting counters
			setAttempts((prev) => prev + 1);
			setLastAttemptTime(Date.now());
		} finally {
			setLoading(false);
		}
	};

	// Clear feedback after 3 seconds
	useEffect(() => {
		if (feedback) {
			const timer = setTimeout(() => setFeedback(""), 3000);
			return () => clearTimeout(timer);
		}
	}, [feedback]);

	return (
		<div className="min-h-screen bg-[#f5e6d3] text-gray-800">
			{/* Title & Subheading */}
			<div className="container mx-auto text-center pt-36 pb-16 px-4 sm:px-8">
				<h1 className="text-4xl md:text-5xl font-bold mb-3">Contact Us</h1>
				<p className="text-lg text-gray-700">
					We'd love to hear from you! Send us a message and we'll get back to
					you as soon as possible.
				</p>
			</div>

			{/* Error Message */}
			{error && (
				<div className="container mx-auto px-4 sm:px-8 mb-4">
					<div className="p-4 bg-red-200 text-red-800 font-semibold text-center rounded transition-opacity duration-500">
						{error}
					</div>
				</div>
			)}

			{/* Feedback Message */}
			{feedback && (
				<div className="container mx-auto px-4 sm:px-8 mb-4">
					<div className="p-4 bg-green-200 text-green-800 font-semibold text-center rounded transition-opacity duration-500">
						{feedback}
					</div>
				</div>
			)}

			{/* Two-Column Layout */}
			<div className="container mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 items-start pb-12 px-4 sm:px-8">
				{/* Left Column: Contact Form */}
				<div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
					<form className="space-y-4" onSubmit={handleSubmit}>
						<div className="flex flex-col md:flex-row gap-4">
							<div className="flex-1">
								<label
									htmlFor="firstName"
									className="block text-sm font-medium mb-1"
								>
									First name *
								</label>
								<input
									id="firstName"
									name="firstName"
									type="text"
									placeholder="First name *"
									className="w-full p-3 border border-gray-300 rounded"
									value={formData.firstName}
									onChange={handleChange}
									required
								/>
							</div>
							<div className="flex-1">
								<label
									htmlFor="lastName"
									className="block text-sm font-medium mb-1"
								>
									Last name *
								</label>
								<input
									id="lastName"
									name="lastName"
									type="text"
									placeholder="Last name *"
									className="w-full p-3 border border-gray-300 rounded"
									value={formData.lastName}
									onChange={handleChange}
									required
								/>
							</div>
						</div>

						<div>
							<label htmlFor="email" className="block text-sm font-medium mb-1">
								Email *
							</label>
							<input
								id="email"
								name="email"
								type="email"
								placeholder="Email *"
								className="w-full p-3 border border-gray-300 rounded"
								value={formData.email}
								onChange={handleChange}
								required
							/>
						</div>

						<div>
							<label htmlFor="phone" className="block text-sm font-medium mb-1">
								Phone *
							</label>
							<input
								id="phone"
								name="phone"
								type="text"
								placeholder="Phone *"
								className="w-full p-3 border border-gray-300 rounded"
								value={formData.phone}
								onChange={handleChange}
								required
							/>
						</div>

						<div>
							<label
								htmlFor="enquiryType"
								className="block text-sm font-medium mb-1"
							>
								Enquiry Type *
							</label>
							<select
								id="enquiryType"
								name="enquiryType"
								className="w-full p-3 border border-gray-300 rounded"
								value={formData.enquiryType}
								onChange={handleChange}
								required
							>
								<option value="Booking">Booking</option>
								<option value="Catering">Catering</option>
								<option value="General Enquiry">General Enquiry</option>
							</select>
						</div>

						<div>
							<label
								htmlFor="message"
								className="block text-sm font-medium mb-1"
							>
								Message *
							</label>
							<textarea
								id="message"
								name="message"
								placeholder="Message *"
								className="w-full p-3 border border-gray-300 rounded h-32"
								value={formData.message}
								onChange={handleChange}
								required
							></textarea>
						</div>

						<button
							type="submit"
							className="w-full p-3 bg-[#596a64] text-white rounded hover:bg-[#46534f] transition-colors disabled:bg-gray-400"
							disabled={loading}
						>
							{loading ? "Sending..." : "Send"}
						</button>
					</form>
				</div>

				{/* Vertical Divider (hidden on mobile) */}
				<div className="hidden md:block w-1 bg-gray-300 h-full mx-auto"></div>

				{/* Right Column: Contact Info / Locations */}
				<div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
					<div className="mt-2">
						<h2 className="text-2xl font-semibold text-center mb-4">
							Our Locations
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{/* Existing location (Greenway) */}
							<div className="p-4 border rounded">
								<div className="flex items-start gap-3">
									<FontAwesomeIcon
										icon={faMapMarkerAlt}
										className="text-xl text-gray-700 mt-1"
									/>
									<div>
										<h3 className="text-lg font-bold">Greenway</h3>
										<p className="text-sm">
											1-172 Anketell and, Reed St S<br />
											Greenway ACT
										</p>
										{/* No opening times provided for this location in the source data */}
									</div>
								</div>
							</div>

							{/* New location (Phillip) */}
							<div className="p-4 border rounded">
								<div className="flex items-start gap-3">
									<FontAwesomeIcon
										icon={faMapMarkerAlt}
										className="text-xl text-gray-700 mt-1"
									/>
									<div>
										<h3 className="text-lg font-bold">Phillip</h3>
										<p className="text-sm">71 Dundas Ct, Phillip ACT 2606</p>
									</div>
								</div>
							</div>
						</div>

						{/* Contact & follow */}
						<div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
							<div className="p-4 border rounded">
								<div className="flex items-start gap-3">
									<FontAwesomeIcon
										icon={faPhone}
										className="text-xl text-gray-700 mt-1"
									/>
									<div>
										<h3 className="text-md font-semibold">Phone</h3>
										<p className="text-sm">Tuggeranong : +61 448 892 194</p>
										<p className="text-sm">Phillip : +61 448 892 194</p>
									</div>
								</div>
							</div>

							<div className="p-4 border rounded">
								<div className="flex items-start gap-3">
									<FontAwesomeIcon
										icon={faEnvelope}
										className="text-xl text-gray-700 mt-1"
									/>
									<div>
										<h3 className="text-md font-semibold">Email</h3>
										<p className="text-sm">info@cafehimalayanbrew.com</p>
										<div className="mt-3 flex gap-4 text-gray-600">
											<a
												href="https://www.facebook.com/people/Cafe-Himalayan-Brew/100094930447837"
												aria-label="Facebook"
												className="hover:text-[#1877F2]"
												target="_blank"
												rel="noopener noreferrer"
											>
												<FontAwesomeIcon icon={faFacebookF} />
											</a>
											<a
												href="https://www.instagram.com/cafehimalayanbrew_tuggeranong/"
												aria-label="Instagram"
												className="hover:text-pink-500"
												target="_blank"
												rel="noopener noreferrer"
											>
												<FontAwesomeIcon icon={faInstagram} />
											</a>
											<a
												href="#"
												className="hover:text-sky-400"
												aria-label="Twitter"
											>
												<FontAwesomeIcon icon={faTwitter} />
											</a>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Contact;
